import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("oed_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const PRODUCTS = [
  {
    key: "tiny-homes",
    title: "Luxury Tiny Homes",
    tag: "01 — Living",
    desc: "Architect-designed compact residences engineered for off-grid autonomy and enduring comfort.",
    img: "https://images.unsplash.com/photo-1643494837039-a8240d7e069b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB0aW55JTIwaG9tZSUyMG1vZGVybiUyMGRhcmslMjBuYXR1cmV8ZW58MHx8fHwxNzg1NjgzODAzfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "geo-domes",
    title: "Geo Domes & Glamping Units",
    tag: "02 — Hospitality",
    desc: "Premium geodesic structures that turn any landscape into a revenue-ready destination.",
    img: "https://images.unsplash.com/photo-1777768785766-db03f4333406?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxnZW9kZXNpYyUyMGRvbWUlMjBnbGFtcGluZyUyMGx1eHVyeSUyMGZvcmVzdHxlbnwwfHx8fDE3ODU2ODM4MDN8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "solar",
    title: "Complete Solar Power Systems",
    tag: "03 — Energy",
    desc: "Turnkey solar arrays delivering true independence from the grid, sized for any build.",
    img: "https://images.unsplash.com/photo-1762506168883-0f829364d598?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxzb2xhciUyMHBvd2VyJTIwbHV4dXJ5JTIwaG9tZSUyMG1vZGVybiUyMGRhcmt8ZW58MHx8fHwxNzg1NjgzODE3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "kitchens",
    title: "Outdoor Kitchens & Living",
    tag: "04 — Lifestyle",
    desc: "Luxury outdoor kitchens and living products that complete the destination experience.",
    img: "https://images.unsplash.com/photo-1571189437635-473398e910c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvdXRkb29yJTIwa2l0Y2hlbiUyMGx1eHVyeSUyMG9mZiUyMGdyaWR8ZW58MHx8fHwxNzg1NjgzODAzfDA&ixlib=rb-4.1.0&q=85",
  },
];

export const EXTRA_PRODUCTS = [
  "Battery Storage Solutions",
  "Water Collection & Filtration",
  "Off-Grid Lifestyle Packages",
  "Sustainable Living Solutions",
];

const HERO = "https://images.unsplash.com/photo-1516794267396-7ca9db9ea79f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwcmVzb3J0JTIwZ2xhbXBpbmclMjBuaWdodCUyMHN0YXJzfGVufDB8fHx8MTc4NTY4MzgxN3ww&ixlib=rb-4.1.0&q=85";

const byKey = Object.fromEntries(PRODUCTS.map((p) => [p.key, p]));

export const PRODUCT_DETAILS = {
  "tiny-homes": {
    subtitle: "Architect-designed off-grid residences",
    blurb: "Turnkey luxury tiny homes engineered for full off-grid autonomy — premium finishes, smart layouts and enduring comfort, ready to live in, host or invest.",
    price: "250 – 350 sq ft",
    gallery: [byKey["tiny-homes"].img, HERO, byKey["kitchens"].img],
    specs: [
      { label: "Footprint", value: "250 – 350 sq ft" },
      { label: "Sleeps", value: "2 – 4 guests" },
      { label: "Power", value: "Off-grid ready" },
      { label: "Delivery", value: "Nationwide, included" },
    ],
    features: ["Full kitchen & bathroom", "Loft bedroom & storage", "Premium, durable finishes", "Energy-efficient envelope"],
  },
  "geo-domes": {
    subtitle: "Premium geodesic glamping units",
    blurb: "Weatherproof, four-season geodesic domes that turn any landscape into a revenue-ready destination guests remember — panoramic, insulated and event-ready.",
    price: "20 – 30 ft diameter",
    gallery: [byKey["geo-domes"].img, HERO, byKey["tiny-homes"].img],
    specs: [
      { label: "Diameter", value: "20 – 30 ft" },
      { label: "Season", value: "4-season rated" },
      { label: "Shell", value: "UV & weatherproof" },
      { label: "Install", value: "Rapid assembly" },
    ],
    features: ["Panoramic viewing windows", "Insulated & climate-ready", "Optimized for nightly revenue", "Event & retreat ready"],
  },
  "solar": {
    subtitle: "Complete solar power systems",
    blurb: "Turnkey solar arrays with battery-ready design and monitoring — true independence from the grid, professionally sized and installed for any build.",
    price: "5 – 15 kW arrays",
    gallery: [byKey["solar"].img, HERO, byKey["geo-domes"].img],
    specs: [
      { label: "Capacity", value: "5 – 15 kW" },
      { label: "Grid", value: "Fully independent" },
      { label: "Panels", value: "Tier-1, 25-yr" },
      { label: "Monitoring", value: "App included" },
    ],
    features: ["Battery-storage ready", "Scalable array design", "Professional installation", "Real-time monitoring"],
  },
  "kitchens": {
    subtitle: "Outdoor kitchens & luxury living",
    blurb: "Modular, weatherproof outdoor kitchens and living products that complete the destination experience — built to entertain and to last.",
    price: "Custom modular",
    gallery: [byKey["kitchens"].img, HERO, byKey["tiny-homes"].img],
    specs: [
      { label: "Build", value: "Modular units" },
      { label: "Rating", value: "Weatherproof" },
      { label: "Appliances", value: "Premium grade" },
      { label: "Layout", value: "Custom" },
    ],
    features: ["Grill & fire features", "Integrated storage", "Ambient lighting", "Durable materials"],
  },
};
